import { useMemo, useRef, useState, useEffect } from 'react';
import '../../accounting/accounting.css';
import '../../modules/SettingsDetailPage.css'; // full-page `.sd-*` detail frame
import './countryDetail.css';
import './viewLock.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ViewToggle } from '../../../components/ViewToggle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useNavActions } from '../../../hooks/useNavActions';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { guardedExit } from '../../../hooks/useUnsavedGuard';
import { useQueryClient } from '@tanstack/react-query';
import { api, runWithAuditReason } from '../../../api/client';
import { useCountries, useSaveCountry, useDeleteCountry, errMessage, type Country } from './systemMasters';
import { CountryStatesPanel } from './CountryStatesPanel';
import type { StagedState } from './city';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ System Management ▸ Country — CRUD over the legacy dbo.CustomCountry
 * master, mirroring the XAF `CustomCountry_ListView` / detail: a grid of
 * EnglishLongName + EnglishShortName + NationalFlag. The row icon opens the Detail View in edit mode
 * (the in-row editor was removed 2026-10-14); New / Edit / Delete / Refresh / Export, and an Audit trail.
 */
export function CountryPage({ onHome }: Props) {
  const notify = useNotify();
  // Module Access Control: hide (never grey) the actions the signed-in role does not hold.
  const perm = useNavActions('sm-country');
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useCountries();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveCountry();
  const del = useDeleteCountry();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  const [editing, setEditing] = useState<Country | null | undefined>(undefined);
  const [longName, setLongName] = useState('');
  const [shortName, setShortName] = useState('');
  const [flag, setFlag] = useState<string | null>(null);   // NationalFlag as base64 (no data-URI prefix)
  const flagInputRef = useRef<HTMLInputElement>(null);     // hidden file picker behind the edit pencil
  const [err, setErr] = useState('');
  // States staged in memory while creating a brand-new country (LIMS parity) —
  // the whole Country→State→City→Area chain commits when the country is saved.
  const [stagedStates, setStagedStates] = useState<StagedState[]>([]);
  // View → Edit (owner request 2026-10-12): a click on a row opens the record READ-ONLY with an Edit
  // button; Edit turns the boxes and the Save buttons on. New always opens editable.
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const viewOnly = mode === 'view' && !!editing?.id;
  const openNew = () => { setMode('edit'); setEditing(null); setLongName(''); setShortName(''); setFlag(null); setErr(''); setStagedStates([]); };
  const openView = (r: Country) => { openEdit(r); setMode('view'); };
  const openEdit = (r: Country) => { setMode(perm.write ? 'edit' : 'view'); setEditing(r); setLongName(r.longName ?? ''); setShortName(r.shortName ?? ''); setFlag(r.nationalFlag ?? null); setErr(''); setStagedStates([]); };
  // Read a chosen image file into base64 (strip the data-URI prefix the API doesn't want).
  const onFlagFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFlag(String(reader.result).split(',')[1] ?? null);
    reader.readAsDataURL(file);
  };
  const close = () => { setEditing(undefined); setStagedStates([]); };
  // Cancel / breadcrumb / record arrows: ask before dropping unsaved edits (standing rule 15).
  const leave = () => { void guardedExit(close); };

  // Toolbar Edit: the ONE ticked row opens in the Detail View, editable (owner request 2026-10-14 — the
  // in-row editor was removed; the row icon opens the same Detail View).
  const editSelected = () => {
    if (selectedIds.size !== 1) return;
    const [id] = [...selectedIds];
    const r = rows.find((x) => x.id === id);
    if (r) openEdit(r);
  };

  /** Save (create/update) then act per the button: stay open, close, or reset to New (LIMS detail parity). */
  const doSave = async (after: 'stay' | 'close' | 'new') => {
    if (!longName.trim()) { setErr('English Long Name must not be empty.'); return; }
    setErr('');
    const wasNew = !editing?.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT; Create is exempt.
    let reason = '';
    if (!wasNew) {
      const r = await gateReason('Country', 'edit');
      if (r === null) return;
      reason = r;
    }
    let newId: string | undefined;
    await runSave({
      save: () => runWithAuditReason(reason, async () => {
        const res = await save.mutateAsync({
          id: editing?.id, longName: longName.trim(), shortName: shortName.trim() || null,
          chineseLongName: editing?.chineseLongName ?? null, chineseShortName: editing?.chineseShortName ?? null,
          nationalFlag: flag,
        });
        newId = editing?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
        // Commit states staged on a brand-new country (create new / re-assign linked),
        // then each state's staged cities, then each city's staged areas — the full
        // Country → State → City → Area chain (LIMS nested-save parity).
        if (wasNew && newId && stagedStates.length) {
          for (const ss of stagedStates) {
            let stateId = ss.stateId;
            if (stateId) {
              await api.put(`/api/v1/system/states/${stateId}`, { longName: ss.name, shortName: ss.shortName ?? null, countryId: newId });
            } else {
              const sres = await api.post('/api/v1/system/states', { longName: ss.name, shortName: ss.shortName ?? null, countryId: newId });
              stateId = (sres as { data?: { data?: { id?: string } } })?.data?.data?.id;
            }
            if (stateId && ss.cities?.length) {
              for (const sc of ss.cities) {
                let cityId = sc.cityId;
                if (cityId) {
                  await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId });
                } else {
                  const cres = await api.post('/api/v1/cities', { cityName: sc.name, stateId });
                  cityId = (cres as { data?: { data?: { id?: string } } })?.data?.data?.id;
                }
                if (cityId && sc.areas?.length) {
                  for (const a of sc.areas) {
                    if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                    else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
                  }
                }
              }
            }
          }
          // The staged states/cities/areas were committed via raw api.post AFTER the
          // country-save invalidation fired — refresh those caches so the grids show
          // the committed rows instead of the stale (empty) list.
          await qc.invalidateQueries({ queryKey: ['sm-states-full'] });
          await qc.invalidateQueries({ queryKey: ['sm-cities'] });
          await qc.invalidateQueries({ queryKey: ['sm-city-areas'] });
        }
      }),
      successMessage: 'Saved successfully.',
      onSaved: () => {
        if (after === 'close') { close(); return; }
        if (after === 'new') { openNew(); return; }
        if (wasNew && newId) setEditing({ id: newId, longName: longName.trim(), shortName: shortName.trim() || null, chineseLongName: null, chineseShortName: null, nationalFlag: flag });
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one country to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${selectedIds.size} countr${selectedIds.size === 1 ? 'y' : 'ies'}?` })) return;
    const reason = await gateReason('Country', 'delete');
    if (reason === null) return;
    try { await runWithAuditReason(reason, async () => { for (const id of selectedIds) await del.mutateAsync(String(id)); }); setSelectedIds(new Set()); await alert({ title: 'Success', message: 'Deleted.', tone: 'success' }); }
    catch (e) { notify.error(errMessage(e)); }
  };

  // Detail shortcuts through the shared hotkey layer (standing rule 14).
  useHotkeys(editing === undefined ? [] : viewOnly ? [
    ...(perm.write ? [
      { combo: 'alt+e', label: 'Edit', group: 'Country', order: 1, run: () => setMode('edit') },
      { combo: 'f2', label: 'Edit', group: 'Country', order: 2, run: () => setMode('edit') },
    ] : []),
    { combo: 'esc', label: 'Close', group: 'Country', order: 3, allowInInput: false, run: close },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Country', order: 1, run: () => void doSave('stay') },
    ...(perm.create ? [{ combo: 'mod+shift+s', label: 'Save & New', group: 'Country', order: 2, run: () => void doSave('new') }] : []),
    { combo: 'mod+enter', label: 'Save & Close', group: 'Country', order: 3, run: () => void doSave('close') },
    { combo: 'esc', label: 'Cancel', group: 'Country', order: 4, allowInInput: false, run: leave },
  ]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => [r.longName, r.shortName].some((v) => (v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  const cmp = (get: (r: Country) => string | null) => (a: Country, b: Country) =>
    (get(a) ?? '').localeCompare(get(b) ?? '', undefined, { sensitivity: 'base' });

  const columns: AppColumn<Country>[] = [
    // Opens the EDITABLE Detail View (owner request 2026-10-14; was the in-row editor) — hidden for a role without Write.
    ...(perm.write ? [{
      field: 'detailedit', header: '', noExport: true, sortable: false, filterable: false, width: 44, align: 'center' as const, text: () => '',
      render: (r: Country) => (
        <button className="pts-icon-btn edit" title="Edit" aria-label={`Edit ${r.longName ?? 'country'}`}
          onClick={(e) => { e.stopPropagation(); openEdit(r); }}><i className="ti ti-edit" /></button>
      ),
    }] : []),
    {
      field: 'longName', header: 'EnglishLongName', minWidth: 300, text: (r) => r.longName ?? '', compare: cmp((r) => r.longName),
      render: (r) => <span>{r.longName ?? ''}</span>,
    },
    {
      field: 'shortName', header: 'EnglishShortName', minWidth: 300, sortable: false, text: (r) => r.shortName ?? '',
      render: (r) => <span>{r.shortName ?? ''}</span>,
    },
    {
      // NationalFlag (legacy column) — the stored flag image, blank when none (LIMS parity).
      field: 'nationalFlag', header: 'NationalFlag', minWidth: 300, sortable: false, filterable: false,
      text: () => '',
      render: (r) => (r.nationalFlag ? <img className="cty-flag" src={`data:image;base64,${r.nationalFlag}`} alt="" /> : null),
    },
  ];

  // ── Full-page detail (New / Edit) — replaces the list while open (standing rule 12; owner
  //    request 2026-10-09: New must open the detail view, not a popup). Same frame as the City
  //    and Division forms: title + breadcrumb + record arrows, action bar top and bottom. ──
  if (editing !== undefined) {
    const recordName = editing ? (editing.longName ?? '').trim() || 'Country' : 'New Country';
    const curIdx = editing ? rows.findIndex((r) => r.id === editing.id) : -1;
    const prevRow = curIdx > 0 ? rows[curIdx - 1] : null;
    const nextRow = curIdx >= 0 && curIdx < rows.length - 1 ? rows[curIdx + 1] : null;
    const actions = (bottom?: boolean) => viewOnly ? (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        {perm.write && <button className="sd-abtn primary" onClick={() => setMode('edit')} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>}
        <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
      </div>
    ) : (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        <button className="sd-abtn primary" onClick={() => void doSave('stay')} disabled={save.isPending} title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
        <button className="sd-abtn" onClick={() => void doSave('close')} disabled={save.isPending} title={`Save and Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save and Close</button>
        {perm.create && <button className="sd-abtn" onClick={() => void doSave('new')} disabled={save.isPending} title={`Save and New (${formatCombo('mod+shift+s')})`}><span className="tb-i">📄</span> Save and New</button>}
        <span className="sd-abtn-sep" aria-hidden="true" />
        <button className="sd-abtn cancel" onClick={leave} disabled={save.isPending} title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
      </div>
    );
    return (
      <div className="bl-page mlv-host">
        {/* List/Detail switch (as City / Payment Terms Setup): "List View" goes back to the list, asking first when
            there are unsaved changes. */}
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
        </div>
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>Country – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                  <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
                  <a onClick={leave}>Country</a><i className="ti ti-chevron-right" /><span>{recordName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button className="sd-na" title="Previous record" disabled={!prevRow || save.isPending} onClick={() => prevRow && void guardedExit(() => (viewOnly ? openView : openEdit)(prevRow))}>‹</button>
                <button className="sd-na next" title="Next record" disabled={!nextRow || save.isPending} onClick={() => nextRow && void guardedExit(() => (viewOnly ? openView : openEdit)(nextRow))}>›</button>
              </div>
            </div>

            {actions()}

            <div className="sd-body cty-detail">
              <fieldset className="sm-viewlock cty-fields" disabled={viewOnly}>
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">English Long Name<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={longName} onChange={(e) => { setLongName(e.target.value); if (err) setErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void doSave('stay'); }} />
                  {err && <div className="acc-err">{err}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">English Short Name</label>
                <div className="acc-fh-control">
                  <input className="acc-input" value={shortName} onChange={(e) => setShortName(e.target.value)} />
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">National Flag</label>
                <div className="acc-fh-control">
                  {/* No image → file textbox + Browse…; after upload → preview + edit/erase (XAF ImageEditor). */}
                  {flag ? (
                    <>
                      <img className="cty-flag-lg" src={`data:image;base64,${flag}`} alt="National flag preview" />
                      <div className="cty-flag-acts">
                        <button className="cty-flag-btn" title="Edit / choose image" onClick={() => flagInputRef.current?.click()}><i className="ti ti-pencil" /></button>
                        <button className="cty-flag-btn" title="Erase" onClick={() => setFlag(null)}><i className="ti ti-eraser" /></button>
                      </div>
                    </>
                  ) : (
                    <div className="acc-lookup">
                      <input className="acc-input" readOnly value="" placeholder="" onClick={() => flagInputRef.current?.click()} />
                      <button className="acc-browse-btn" onClick={() => flagInputRef.current?.click()}>Browse…</button>
                    </div>
                  )}
                  <input ref={flagInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => { onFlagFile(e.target.files?.[0]); e.target.value = ''; }} />
                </div>
              </div>
              </fieldset>
              {/* State child collection — Country → State (one-to-many). Mirrors
                  CustomCountry_DetailView ▸ State; activates once the country is saved. */}
              <div className={viewOnly ? 'sm-viewlock-panel' : undefined}>
                <CountryStatesPanel countryId={editing?.id} staged={stagedStates} onStaged={setStagedStates} />
              </div>
            </div>

            {actions(true)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Country</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        {/* Breadcrumb on top, then the List/Detail switch beside the FormTitle (as City / Payment Terms Setup, owner
            request 2026-10-15). "Detail View" opens the ticked record — or the first one — read-only. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <ViewToggle view="list" detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== 'detail') return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id); if (r) openView(r);
            }} />
          <FormTitle pageId="sm-country" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Country" />
        </div>
      </div>
      <div className="acc-body acc-search-tight">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6); Export far right. */}
        {!gridMax && (
        <AppToolbar
          onNew={perm.create ? openNew : undefined}
          onEdit={perm.write ? editSelected : undefined} editDisabled={selectedIds.size !== 1}
          onDelete={perm.delete ? () => void removeSelected() : undefined} deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()} refreshing={isFetching}
          // Export to on the action bar (owner request 2026-10-09); Search sits on the search row above the grid (2026-10-14).
          right={
            <>
              <ExportMenu triggerClassName="ca-btn" filename="Country" headers={['EnglishLongName', 'EnglishShortName']} rows={visible.map((r) => [r.longName ?? '', r.shortName ?? ''])} />
            </>
          }
        />
        )}
        {/* ONE white grid card — grid + its single footer only. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="Country" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card">
          <AppDataGrid<Country> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => openView(r)}
            initialSort={{ key: 'longName', dir: 'asc' }} loading={isFetching} emptyText="No countries found." hideHeaderTools />
        </div>
      </div>

    </div>
  );
}
